import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  Flag,
  FolderKanban,
  Loader2,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import {
  useDeleteMilestone,
  useMilestoneList,
  useToggleMilestoneFlag,
  useUpdateMilestoneStatus,
} from "../hooks/useMilestones";
import { NewMilestoneDrawer } from "../components/NewMilestoneDrawer";
import { MilestoneStatusBadge } from "../components/MilestoneStatusBadge";
import { MilestonePriorityBadge } from "../components/MilestonePriorityBadge";
import { MilestoneProgressBar } from "../components/MilestoneProgressBar";
import type { MilestoneListItem } from "../types";

const BORDER = "hsl(210,30%,85%)";

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

const MILESTONE_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "category", label: "Category", defaultVisible: false },
  { key: "context", label: "Context" },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "progress", label: "Progress" },
  { key: "dueDate", label: "Due Date" },
  { key: "ownerName", label: "Owner" },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "at_risk", label: "At Risk" },
  { value: "delayed", label: "Delayed" },
  { value: "cancelled", label: "Cancelled" },
];

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function MilestoneListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [projectFilter, setProjectFilter] = useState("");
  const [meetingFilter, setMeetingFilter] = useState("");
  const [ideaFilter, setIdeaFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [statusPopover, setStatusPopover] = useState<string | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("milestones", MILESTONE_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      category: categoryFilter || undefined,
      projectId: projectFilter || undefined,
      meetingId: meetingFilter || undefined,
      ideaId: ideaFilter || undefined,
    }),
    [
      search,
      statusFilter,
      priorityFilter,
      categoryFilter,
      projectFilter,
      meetingFilter,
      ideaFilter,
    ],
  );

  const { data, isLoading, isError, error } = useMilestoneList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    category: categoryFilter || undefined,
    projectId: projectFilter || undefined,
    meetingId: meetingFilter || undefined,
    ideaId: ideaFilter || undefined,
    isFlagged: flaggedOnly ? true : undefined,
  });
  const { data: projectsResult } = useProjectList({ page: 1, limit: 200 });
  const projects = projectsResult?.data ?? [];
  const { data: meetingsResult } = useMeetingList({ page: 1, limit: 100 });
  const meetings = meetingsResult?.data ?? [];
  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const ideas = ideasResult?.data ?? [];
  const { data: categoryOptions = [] } = useCategoryItems("milestone_category");

  const updateStatus = useUpdateMilestoneStatus();
  const toggleFlag = useToggleMilestoneFlag();
  const deleteMilestone = useDeleteMilestone();

  const milestones = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleStatusChange(id: string, status: string) {
    await updateStatus.mutateAsync({ id, status });
    setStatusPopover(null);
    showToast(`Status updated`);
  }

  async function handleToggleFlag(m: MilestoneListItem) {
    await toggleFlag.mutateAsync({ id: m.id, isFlagged: !m.isFlagged });
  }

  function handleDelete(m: MilestoneListItem) {
    setDeleteTarget({ id: m.id, name: m.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteMilestone.mutateAsync(deleteTarget.id);
    showToast("Milestone deleted");
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Milestones" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flag
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">
              Milestones
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="milestones"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={MILESTONE_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Milestone
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load milestones</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search milestones..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <Select
                value={priorityFilter}
                onChange={(e) => resetPage(setPriorityFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Priority</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Select>
              <Select
                value={categoryFilter}
                onChange={(e) => resetPage(setCategoryFilter)(e.target.value)}
                className="w-44 shrink-0"
              >
                <option value="">All Categories</option>
                {categoryOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <button
                type="button"
                onClick={() => resetPage(setFlaggedOnly)(!flaggedOnly)}
                className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors"
                style={{
                  borderColor: flaggedOnly
                    ? "hsl(40,95%,60%)"
                    : "hsl(210,30%,85%)",
                  background: flaggedOnly ? "hsl(40,95%,95%)" : "white",
                  color: flaggedOnly ? "hsl(40,95%,30%)" : "hsl(215,16%,47%)",
                }}
                title="Flagged only"
              >
                <Flag
                  className="h-[14px] w-[14px]"
                  strokeWidth={1.5}
                  style={{ fill: flaggedOnly ? "hsl(40,95%,55%)" : "none" }}
                />
                Flagged
              </button>
              <Select
                value={projectFilter}
                onChange={(e) => resetPage(setProjectFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Select
                value={meetingFilter}
                onChange={(e) => resetPage(setMeetingFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Meetings</option>
                {meetings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.title}
                  </option>
                ))}
              </Select>
              <Select
                value={ideaFilter}
                onChange={(e) => resetPage(setIdeaFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Ideas</option>
                {ideas.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.code} — {i.title}
                  </option>
                ))}
              </Select>
            </div>

            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading milestones…
                </div>
              ) : milestones.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No milestones match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          #
                        </th>
                      )}
                      {isVisible("title") && (
                        <th className="px-[14px] py-[9px]">Title</th>
                      )}
                      {isVisible("category") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Category
                        </th>
                      )}
                      {isVisible("context") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 170 }}>
                          Context
                        </th>
                      )}
                      {isVisible("priority") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 90 }}>
                          Priority
                        </th>
                      )}
                      {isVisible("status") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Status
                        </th>
                      )}
                      {isVisible("progress") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Progress
                        </th>
                      )}
                      {isVisible("dueDate") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          Due Date
                        </th>
                      )}
                      {isVisible("ownerName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 140 }}>
                          Owner
                        </th>
                      )}
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 150 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {milestones.map((m) => {
                      const isOverdue =
                        !!m.dueDate &&
                        new Date(m.dueDate) < new Date() &&
                        m.status !== "completed" &&
                        m.status !== "cancelled";
                      const ContextIcon = m.projectId
                        ? FolderKanban
                        : m.productId
                          ? Package
                          : m.meetingId
                            ? CalendarDays
                            : Building2;
                      const contextName =
                        m.projectName ??
                        m.productName ??
                        m.meetingTitle ??
                        m.companyName ??
                        null;
                      return (
                        <tr
                          key={m.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/milestones/${m.id}`)}
                        >
                          {isVisible("code") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {m.code}
                            </td>
                          )}
                          {isVisible("title") && (
                            <td className="px-[14px] py-[11px]">
                              <Link
                                to={`/milestones/${m.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                              >
                                {m.isFlagged && (
                                  <Flag
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                    style={{
                                      color: "hsl(40,95%,45%)",
                                      fill: "hsl(40,95%,55%)",
                                    }}
                                  />
                                )}
                                {truncate(m.title, 50)}
                              </Link>
                            </td>
                          )}
                          {isVisible("category") && (
                            <td className="px-[14px] py-[11px]">
                              {m.category ? (
                                <span
                                  className="inline-flex rounded-full px-2 py-0.5 text-[11px] text-muted-foreground capitalize"
                                  style={{ background: "hsl(210,16%,95%)" }}
                                >
                                  {m.category.replace(/_/g, " ")}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("context") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {contextName ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <ContextIcon
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                  />
                                  <span className="truncate">{contextName}</span>
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("priority") && (
                            <td className="px-[14px] py-[11px]">
                              <MilestonePriorityBadge priority={m.priority} />
                            </td>
                          )}
                          {isVisible("status") && (
                            <td className="px-[14px] py-[11px]">
                              <MilestoneStatusBadge status={m.status} />
                            </td>
                          )}
                          {isVisible("progress") && (
                            <td className="px-[14px] py-[11px]">
                              <div className="flex items-center gap-2">
                                <MilestoneProgressBar
                                  progress={m.progress}
                                  status={m.status}
                                  size="md"
                                />
                                <span className="text-[12px] text-muted-foreground">
                                  {m.progress}%
                                </span>
                              </div>
                            </td>
                          )}
                          {isVisible("dueDate") && (
                            <td className="px-[14px] py-[11px]">
                              {m.dueDate ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className={
                                      isOverdue
                                        ? "font-medium text-[hsl(0,70%,45%)]"
                                        : "text-foreground"
                                    }
                                  >
                                    {formatDate(m.dueDate)}
                                  </span>
                                  {isOverdue && (
                                    <span
                                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                      style={{
                                        background: "hsl(0,70%,94%)",
                                        color: "hsl(0,70%,40%)",
                                      }}
                                    >
                                      Overdue
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("ownerName") && (
                            <td className="px-[14px] py-[11px]">
                              {m.ownerName ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                    style={{
                                      background: "hsl(210,55%,92%)",
                                      color: "hsl(210,100%,28%)",
                                    }}
                                  >
                                    {initialsOf(m.ownerName)}
                                  </span>
                                  <span className="text-foreground">
                                    {m.ownerName}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  Unassigned
                                </span>
                              )}
                            </td>
                          )}
                          <td
                            className="relative px-[14px] py-[11px] text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/milestones/${m.id}`);
                                }}
                              >
                                <Eye
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/milestones/${m.id}`);
                                }}
                              >
                                <Pencil
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title={m.isFlagged ? "Unflag" : "Flag"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFlag(m);
                                }}
                              >
                                <Flag
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                  style={{
                                    color: m.isFlagged
                                      ? "hsl(40,95%,45%)"
                                      : undefined,
                                    fill: m.isFlagged
                                      ? "hsl(40,95%,55%)"
                                      : "none",
                                  }}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Change Status"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatusPopover(
                                    statusPopover === m.id ? null : m.id,
                                  );
                                }}
                              >
                                <RefreshCw
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnDelete}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(m);
                                }}
                              >
                                <Trash2
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                            </div>
                            {statusPopover === m.id && (
                              <div
                                className="absolute right-4 top-10 z-20 w-56 rounded-lg border bg-white p-2 shadow-lg"
                                style={{ borderColor: BORDER }}
                              >
                                {STATUS_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() =>
                                      handleStatusChange(m.id, opt.value)
                                    }
                                    className="block w-full rounded px-3 py-1.5 text-left text-sm text-foreground hover:bg-[hsl(210,50%,96%)]"
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewMilestoneDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Milestone created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete milestone?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Milestones list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
